/**
 * Infer books that share a series folder from the canonical Postgres-backed folder/project maps.
 * Used when Firestore `project.seriesId` is stale/absent or `useSeriesStore` was cleared (e.g. home navigation).
 */
export type MentionSeriesFolderBook = {
  projectId: string;
  title: string;
  seriesOrder?: number | null;
};

type FolderLike = { id: string; series_id?: string | null };

type ProjectRowLike = {
  firebase_id?: string;
  folder_id?: string;
  deleted?: boolean;
  archived?: boolean;
  title?: string;
  series_order?: number | string | null;
};

function parseSeriesOrder(value: unknown): number | null {
  if (value === null || value === undefined) return null;
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : null;
}

export function resolveSeriesBooksFromFolderProjects(
  firebaseProjectId: string | undefined,
  foldersById: Record<string, FolderLike> | undefined,
  projectsById: Record<string, ProjectRowLike> | undefined,
): MentionSeriesFolderBook[] {
  const pid = typeof firebaseProjectId === "string" ? firebaseProjectId.trim() : "";
  if (!pid || !foldersById || !projectsById) return [];

  let currentRow: ProjectRowLike | undefined;
  for (const row of Object.values(projectsById)) {
    if (typeof row?.firebase_id !== "string") continue;
    if (row.firebase_id.trim() !== pid) continue;
    currentRow = row;
    break;
  }

  if (!currentRow?.folder_id) return [];

  const folder = foldersById[currentRow.folder_id];
  const prismaSeriesId = typeof folder?.series_id === "string" ? folder.series_id.trim() : "";

  if (!prismaSeriesId) return [];

  const books: MentionSeriesFolderBook[] = [];

  for (const row of Object.values(projectsById)) {
    if (row?.deleted || row?.archived) continue;
    const fbId = typeof row.firebase_id === "string" ? row.firebase_id.trim() : "";
    const fid = typeof row.folder_id === "string" ? row.folder_id.trim() : "";
    if (!fbId || !fid) continue;

    const rowFolder = foldersById[fid];
    const rowSeries =
      typeof rowFolder?.series_id === "string" ? rowFolder.series_id.trim() : "";
    if (rowSeries !== prismaSeriesId) continue;

    const title =
      typeof row.title === "string" && row.title.trim()
        ? row.title.trim()
        : "Untitled Book";

    books.push({
      projectId: fbId,
      title,
      seriesOrder: parseSeriesOrder(row.series_order),
    });
  }

  books.sort((a, b) => {
    const ao = a.seriesOrder ?? Number.POSITIVE_INFINITY;
    const bo = b.seriesOrder ?? Number.POSITIVE_INFINITY;
    if (ao !== bo) return ao - bo;
    return a.title.localeCompare(b.title);
  });

  return books;
}
